import capitalize from 'lodash/capitalize'
import groupBy from 'lodash/groupBy'
import isEqual from 'lodash/isEqual'
import { analytics, EVENT_NAMES } from './Analytics'
import { trackOrderCompleted } from './orderAnalytics.js'
import {
  getNumericValue,
  calculatePercentage,
  unionArrayValues,
} from './AnalyticsUtils'
import { MERGE_TRACKING_DATA_STRATEGIES } from './orderGroupAnalytics.consts'
import { PRODUCT_TYPES } from '../ProductTypeState'
import { logger } from '../logger'
import OrderManager from '../OrderManager'
import UserManager from '../UserManager'
import { roundDecimal, roundedDecimalSumBy } from '../../utils/utils'
import { extractErrorMessage } from '../../utils/ApiUtils'
import { CROP_TYPE } from '../../utils/cropUtils.consts'
import {
  isProductItem,
  isShippingItem,
  isTaxItem,
} from '../../features/cart/groupCheckoutPricing.utils'
import type {
  GroupOrderParams,
  InnerOrder,
  OrderGroup,
} from '../GroupOrderManager.types'

export type OrderTrackingData = Record<string, unknown>

// One sub-order's analytics, bundled so its product type / order / tracking
// data travel together. Downstream steps group and merge over these records
// instead of indexing parallel arrays by position — the coupling that desynced
// once `partials` was filtered for failed builds.
export type SubOrderPartial = {
  productType: string
  order: InnerOrder
  trackingData: OrderTrackingData
  contentParams?: unknown
}

type CartPayload = GroupOrderParams['cartPayloads'][number]
type OrderPriceSummary = GroupOrderParams['orderPriceSummaries'][number]

// Fail fast if the per-sub-order input arrays aren't all 1:1 with cartPayloads,
// e.g. 2 cart payloads must map to exactly 2 orders and 2 price summaries.
function assertMatchingGroupOrderAnalyticsInputs({
  cartPayloads,
  orders,
  orderRequestParams,
  orderPriceSummaries,
}: {
  cartPayloads: GroupOrderParams['cartPayloads']
  orders: InnerOrder[]
  orderRequestParams: Record<string, unknown>[]
  orderPriceSummaries: GroupOrderParams['orderPriceSummaries']
}) {
  const expected = cartPayloads.length
  const lengths = {
    orders: orders.length,
    orderRequestParams: orderRequestParams.length,
    orderPriceSummaries: orderPriceSummaries.length,
  }

  Object.entries(lengths).forEach(([name, length]) => {
    if (length !== expected) {
      throw new Error(
        `Group order analytics expected ${expected} ${name}, received ${length}`
      )
    }
  })
}

// The sub-order response's summaryItems (e.g. its product / shipping / tax lines),
// or throw if the server omitted them.
function getResponseSummaryItems(responseOrder: InnerOrder) {
  if (!Array.isArray(responseOrder.summaryItems)) {
    throw new Error(
      `Group order analytics missing summaryItems for sub-order ${responseOrder?.uid}`
    )
  }
  return responseOrder.summaryItems
}

// The sub-order's single shipping line (e.g. "Standard $4.99") — throws unless
// exactly one is present.
function getResponseShippingItem(responseOrder: InnerOrder) {
  const summaryItems = getResponseSummaryItems(responseOrder)
  const shippingItems = summaryItems.filter(isShippingItem)
  if (shippingItems.length !== 1) {
    throw new Error(
      `Group order analytics expected one shipping item for sub-order ${responseOrder.uid}, received ${shippingItems.length}`
    )
  }
  return shippingItems[0]
}

// Reconstruct one sub-order's price summary from its server response + the base
// checkout summary, e.g. the tiles order's own totals/tax from the response, the
// shared group-level gift-card fields from the base.
export function buildOrderPriceSummaryFromResponse({
  baseSummary,
  responseOrder,
  productType,
}: {
  baseSummary: OrderPriceSummary
  responseOrder: InnerOrder
  productType: string
}) {
  const summaryItems = getResponseSummaryItems(responseOrder)
  const productItems = summaryItems.filter(isProductItem)
  if (productItems.length === 0) {
    throw new Error(
      `Group order analytics missing product items for sub-order ${responseOrder.uid}`
    )
  }

  const shippingItem = getResponseShippingItem(responseOrder)
  const tax = summaryItems
    .filter(isTaxItem)
    .reduce((sum, item) => sum + item.price, 0)
  const shippingCost = shippingItem.priceAfterDiscount
  const shippingCostBeforeDiscount = shippingItem.priceBeforeDiscount
  const productTotalBeforeDiscount = productItems.reduce(
    (sum, p) => sum + p.priceBeforeDiscount,
    0
  )
  const productTotalAfterDiscount = productItems.reduce(
    (sum, p) => sum + p.priceAfterDiscount,
    0
  )
  // Discount across discountable lines (products + shipping): total list
  // price minus total charged. Matches formatPricingSummary so
  // 'Cart Value … Original' includes shipping.
  const priceBeforeDiscount =
    productTotalBeforeDiscount + shippingCostBeforeDiscount
  const priceAfterDiscount = productTotalAfterDiscount + shippingCost
  const discountPrice = priceBeforeDiscount - priceAfterDiscount

  return {
    ...baseSummary,
    productType,
    validated: true,
    currency: responseOrder.currency,
    totalPrice: responseOrder.totalCost,
    priceBeforeTax:
      responseOrder.clientPriceBeforeTax ??
      productTotalAfterDiscount + shippingCost,
    discountPrice,
    productTotalPrice: productTotalAfterDiscount,
    productTotalPriceBeforeDiscount: productTotalBeforeDiscount,
    shippingCost,
    shippingCostBeforeDiscount,
    tax,
    summaryItems,
    products: productItems,
    freeTilesData: baseSummary?.freeTilesData ?? {
      freeTilesCreditUsed: 0,
    },
  }
}

// Stamp the sub-order's product pricing onto each cart orderItem, e.g. set each
// item's totalPrice to the order's product total before discount.
function buildOrderItemsForAnalytics({
  payload,
  productTotalBeforeDiscount,
  productTotalAfterDiscount,
  currency,
}: {
  payload: CartPayload
  productTotalBeforeDiscount: number
  productTotalAfterDiscount: number
  currency: string
}) {
  if (!Array.isArray(payload.orderItems)) {
    throw new Error(
      `Group order analytics missing orderItems for ${payload.productType}`
    )
  }

  return (payload.orderItems as Array<Record<string, unknown>>).map((item) => ({
    ...item,
    totalPrice: {
      value: productTotalBeforeDiscount,
      currency,
    },
    priceAfterDiscount: productTotalAfterDiscount,
  }))
}

// Build one sub-order's Order Completed tracking data via the single-order builder,
// e.g. compute the tiles sub-order's own "USD Revenue" and "Photo Count", just as a
// standalone tiles order would report them.
function buildSubOrderTrackingPartial({
  payload,
  responseOrder,
  requestParams,
  baseSummary,
  orderGroupUid,
  deliveryPromise,
  paymentMethod,
  address,
  emailAddedRecently,
  email,
}: {
  payload: CartPayload
  responseOrder: InnerOrder
  requestParams: Record<string, unknown>
  baseSummary: OrderPriceSummary
  orderGroupUid: string
  deliveryPromise: GroupOrderParams['deliveryPromise']
  paymentMethod: GroupOrderParams['paymentMethod']
  address: GroupOrderParams['address']
  emailAddedRecently?: boolean
  email: string | undefined
}): SubOrderPartial {
  const orderPriceSummary = buildOrderPriceSummaryFromResponse({
    baseSummary,
    responseOrder,
    productType: payload.productType,
  })
  const orderItems = buildOrderItemsForAnalytics({
    payload,
    productTotalBeforeDiscount:
      orderPriceSummary.productTotalPriceBeforeDiscount,
    productTotalAfterDiscount: orderPriceSummary.productTotalPrice,
    currency: orderPriceSummary.currency,
  })

  const trackingResult = OrderManager.buildOrderCompletedTrackingData({
    resultOrder: responseOrder,
    totalPrice: orderPriceSummary.totalPrice,
    tax: orderPriceSummary.tax,
    shippingCost: orderPriceSummary.shippingCost,
    currency: orderPriceSummary.currency,
    tiles: payload.orderTiles ?? [],
    orderData: { ...requestParams, productType: payload.productType },
    deliveryPromise,
    paymentMethod,
    address,
    orderItems,
    orderPriceSummary,
    productType: payload.productType,
    emailAddedRecently,
    email,
    orderGroupUid,
  })
  if (!trackingResult) {
    throw new Error(
      `Failed to build tracking data for sub-order ${responseOrder?.uid} (${payload.productType})`
    )
  }

  return {
    productType: payload.productType,
    order: responseOrder,
    trackingData: trackingResult.orderTrackingData,
    contentParams: trackingResult.contentParams,
  }
}

// Group-level price summary: base summary with group currency and summed shipping,
// e.g. shippingCost = $4.99 + $0 across two sub-orders.
function buildCombinedOrderPriceSummary({
  orderPriceSummaries,
  orders,
  orderGroup,
}: {
  orderPriceSummaries: GroupOrderParams['orderPriceSummaries']
  orders: InnerOrder[]
  orderGroup: OrderGroup
}) {
  const groupShippingCost = roundedDecimalSumBy(
    orders,
    (order: InnerOrder) => getResponseShippingItem(order).priceAfterDiscount,
    2
  )

  return {
    ...orderPriceSummaries[0],
    currency: orderGroup.currency,
    shippingCost: groupShippingCost,
  }
}

// Combine the sub-order partials, add the per-product-type breakdown, then layer
// bag analytics, e.g. sum the sub-orders into group totals, add each type's revenue
// like "Classic USD Revenue", then the cart-level "Cart Items Count".
export function buildGroupOrderCompletedTrackingData({
  partials,
  orderGroup,
  bagAnalytics,
  address,
}: {
  partials: SubOrderPartial[]
  orderGroup: OrderGroup
  bagAnalytics?: GroupOrderParams['bagAnalytics']
  address: GroupOrderParams['address']
}): OrderTrackingData {
  const merged = combineSubOrderTrackingDataForGroup(
    partials.map((p) => p.trackingData),
    orderGroup,
    partials.map((p) => p.order)
  )
  return {
    ...merged,
    ...buildPerProductTypeAnalytics(partials),
    ...(bagAnalytics ?? {}),
    'Address Source': address.source,
  }
}

// Entry point: build per-sub-order partials and fire one group Order Completed
// event, e.g. a tiles + photo-book cart becomes a single event with group totals.
export function reportGroupOrderCompletedAnalytics({
  orderGroup,
  orders,
  cartPayloads,
  orderRequestParams,
  orderPriceSummaries,
  address,
  deliveryPromise,
  paymentMethod,
  initialUserParams,
  isFirstOrder,
  emailAddedRecently,
  bagAnalytics,
}: {
  orderGroup: OrderGroup
  orders: InnerOrder[]
  cartPayloads: GroupOrderParams['cartPayloads']
  orderRequestParams: Record<string, unknown>[]
  orderPriceSummaries: GroupOrderParams['orderPriceSummaries']
  address: GroupOrderParams['address']
  deliveryPromise: GroupOrderParams['deliveryPromise']
  paymentMethod: GroupOrderParams['paymentMethod']
  initialUserParams: unknown
  isFirstOrder?: boolean
  emailAddedRecently?: boolean
  bagAnalytics?: GroupOrderParams['bagAnalytics']
}) {
  try {
    assertMatchingGroupOrderAnalyticsInputs({
      cartPayloads,
      orders,
      orderRequestParams,
      orderPriceSummaries,
    })

    const email = UserManager.getUserEmail()
    // Group-order analytics has two levels:
    // 1. Per cart item type: call the single-order builder so Product Ordered,
    //    Tiles Ordered, Artwork Ordered, etc. are emitted for that item.
    // 2. Full cart: combine the sub-order tracking data and send one Order Completed.
    const subOrderPartials = cartPayloads.map((payload, i) =>
      buildSubOrderTrackingPartial({
        payload,
        responseOrder: orders[i],
        requestParams: orderRequestParams[i],
        baseSummary: orderPriceSummaries[i],
        orderGroupUid: orderGroup.uid,
        deliveryPromise,
        paymentMethod,
        address,
        emailAddedRecently,
        email,
      })
    )

    if (subOrderPartials.length === 0) {
      logger.warning(
        'Group order analytics: no partial tracking data assembled',
        { orderGroupUid: orderGroup?.uid }
      )
      return
    }

    const contentParams = subOrderPartials.find((p) => p.contentParams)
      ?.contentParams
    const combinedPriceSummary = buildCombinedOrderPriceSummary({
      orderPriceSummaries,
      orders,
      orderGroup,
    })
    const orderCompletedTrackingData = buildGroupOrderCompletedTrackingData({
      partials: subOrderPartials,
      orderGroup,
      bagAnalytics,
      address,
    })

    trackOrderCompleted({
      orderTrackingData: orderCompletedTrackingData,
      initialUserParams,
      prefix: undefined,
      isFirstOrder,
      orderItems: cartPayloads.flatMap((p) => p.orderItems ?? []),
      emailAddedRecently,
      orderPriceSummary: combinedPriceSummary,
      contentParams,
    })
  } catch (error) {
    analytics.track(EVENT_NAMES.GROUP_ORDER_COMPLETE_REPORT_FAILED, {
      // Pass the message as a string — a raw Error serializes to {} in the
      // analytics payload (message/stack are non-enumerable).
      Error: extractErrorMessage(error),
      orderGroupUid: orderGroup?.uid,
    })
    logger.error('Error reporting group order completed analytics', error)
    // Fail loudly in dev/test so bugs surface; stay non-fatal in prod so a
    // reporting error can't break the already-placed order's completion flow.
    if (import.meta.env.DEV) throw error
  }
}

// Per-product-type breakdown for the merged event. Always emits both keys for
// every known product type — null UID / 0 revenue when that type isn't in the
// order — so Amplitude segmentation never trips on undefined. Revenue reuses
// each partial's already-computed (VAT-adjusted) `USD Revenue` so per-type
// revenues sum to the merged `USD Revenue`. Groups the self-contained partials
// by product type — no positional indexing into parallel arrays.
export function buildPerProductTypeAnalytics(
  partials: SubOrderPartial[]
): OrderTrackingData {
  const byProductType = groupBy(partials, (p) => p.productType)
  const result: OrderTrackingData = {}
  Object.values(PRODUCT_TYPES).forEach((productType) => {
    const label = capitalize(productType)
    const group = byProductType[productType] ?? []
    result[`${label} Order UID`] = group[0]?.order.uid ?? null
    result[`${label} USD Revenue`] = roundedDecimalSumBy(
      group,
      (p: SubOrderPartial) => Number(p.trackingData['USD Revenue']) || 0,
      2
    )
  })
  return result
}

type Conflict = { key: string; values: unknown[] }

type StrategyResult = { values: OrderTrackingData; conflicts: Conflict[] }

// All non-null/undefined values for a key across the partials, e.g. ['USD', 'USD']
// for "Currency" across two sub-orders.
function nonNullValues(trackingDatas: OrderTrackingData[], key: string) {
  return trackingDatas
    .map((d) => d[key])
    .filter((v) => v !== undefined && v !== null)
}

// Resolve a key whose partials are expected to agree: the shared value, a
// conflict when they disagree, or null when no partial has it. Shared by the
// `sameValue` strategy and the uncategorized-key fallback. E.g. "Currency" =
// "USD" on every partial → { value: "USD" }; mixed values → { conflict }.
function resolveAgreedValue(
  trackingDatas: OrderTrackingData[],
  key: string
): { value: unknown } | { conflict: Conflict } | null {
  const values = nonNullValues(trackingDatas, key)
  if (values.length === 0) return null
  if (values.every((v) => isEqual(v, values[0]))) return { value: values[0] }
  return { conflict: { key, values } }
}

// Apply each declared merge strategy to its keys, collecting sameValue conflicts,
// e.g. combine each sub-order's "USD Revenue" into the group total, union their
// "Tile Sizes", and keep the first "Local ID".
function applyDeclaredStrategies(
  trackingDatas: OrderTrackingData[]
): StrategyResult {
  const {
    sum,
    sumAndRound,
    union,
    first: firstKeys,
    sameValue,
    anyTrue,
    firstPresent,
  } = MERGE_TRACKING_DATA_STRATEGIES
  const first = trackingDatas[0]
  const values: OrderTrackingData = {}
  const conflicts: Conflict[] = []

  sum.forEach((key) => {
    values[key] = roundedDecimalSumBy(
      trackingDatas,
      (item: OrderTrackingData) => Number(item[key]) || 0,
      0
    )
  })
  sumAndRound.forEach((key) => {
    values[key] = roundedDecimalSumBy(
      trackingDatas,
      (item: OrderTrackingData) => Number(item[key]) || 0,
      2
    )
  })
  union.forEach((key) => {
    values[key] = unionArrayValues(trackingDatas, key)
  })
  firstKeys.forEach((key) => {
    if (first[key] !== undefined) values[key] = first[key]
  })
  sameValue.forEach((key) => {
    const resolved = resolveAgreedValue(trackingDatas, key)
    if (!resolved) return
    if ('conflict' in resolved) conflicts.push(resolved.conflict)
    else values[key] = resolved.value
  })
  anyTrue.forEach((key) => {
    const present = nonNullValues(trackingDatas, key)
    if (present.includes('True')) {
      values[key] = 'True'
    } else if (present.includes(true)) {
      values[key] = true
    }
  })
  firstPresent.forEach((key) => {
    const value = nonNullValues(trackingDatas, key)[0]
    if (value !== undefined) values[key] = value
  })

  return { values, conflicts }
}

// Per-category AI Templates revenue keys are dynamic (e.g. "AI Templates
// Portrait USD Revenue"), so sum them here instead of via a static strategy.
// The regex skips the base key and the "… Share" ratios.
function mergeDynamicAiTemplateRevenue(
  trackingDatas: OrderTrackingData[]
): OrderTrackingData {
  const aiCategoryRevenueKeys = new Set<string>()
  trackingDatas.forEach((d) =>
    Object.keys(d).forEach((k) => {
      if (/^AI Templates .+ USD Revenue$/.test(k)) aiCategoryRevenueKeys.add(k)
    })
  )
  const values: OrderTrackingData = {}
  aiCategoryRevenueKeys.forEach((key) => {
    values[key] = roundedDecimalSumBy(
      trackingDatas,
      (item: OrderTrackingData) => Number(item[key]) || 0,
      2
    )
  })
  return values
}

// Group-level identifiers — parsed from the orderGroup/orders parameters rather
// than from any partial. `Order UID` mirrors `Order Group UID` so existing
// dashboards keyed on Order UID still resolve; sub-order UIDs are exposed via
// the `Order UIDs` plural array.
function buildGroupIdentifiers(
  orderGroup: OrderGroup,
  orders: InnerOrder[]
): OrderTrackingData {
  return {
    'Order Group UID': orderGroup.uid,
    'Order UIDs': orders.map((o) => o.uid),
    'Order UID': orderGroup.uid,
  }
}

// Ratios recomputed from the merged sums — per-partial values are each a ratio
// against the sub-order's own denominator and can't be averaged or summed.
// Reads the merged-so-far totals and returns only the derived keys, e.g. recompute
// "ArtPhoto USD Revenue Share" as ArtPhoto revenue / total revenue across the whole cart.
function computeDerivedRatios(merged: OrderTrackingData): OrderTrackingData {
  const totalPhotos = getNumericValue(merged, 'Photo Count')
  const ratios: OrderTrackingData = {
    'Will Be Upscaled Percentage': calculatePercentage(
      getNumericValue(merged, 'Will Be Upscaled Count'),
      getNumericValue(merged, 'Order Tiles'),
      roundDecimal
    ),
    'ArtPhoto USD Revenue Share': calculatePercentage(
      getNumericValue(merged, 'ArtPhoto USD Revenue'),
      getNumericValue(merged, 'USD Revenue'),
      roundDecimal
    ),
    'AI Templates USD Revenue Share': calculatePercentage(
      getNumericValue(merged, 'AI Templates USD Revenue'),
      getNumericValue(merged, 'USD Revenue'),
      roundDecimal
    ),
    'Crop Fit Photos Percentage': calculatePercentage(
      getNumericValue(merged, 'Crop Fit Photos Count'),
      totalPhotos
    ),
    // Different denominator: fraction of smart-cropped photos that were
    // manually adjusted afterwards, not total photos.
    'Smart Crop Adjusted Percentage': calculatePercentage(
      getNumericValue(merged, 'Smart Crop Adjusted Count'),
      getNumericValue(merged, 'Smart Cropped Photos Count')
    ),
  }

  // Per-crop-type percentages — recomputed across the entire order (all product
  // types: tiles + photo books + ...) using the merged Photo Count as the
  // denominator. Generated from CROP_TYPE so the four cases stay in sync with
  // getCropsDistribution() in orderAnalytics.js.
  Object.values(CROP_TYPE).forEach((cropType: string) => {
    const prefix = capitalize(cropType)
    ratios[`${prefix} Cropped Photos Percentage`] = calculatePercentage(
      getNumericValue(merged, `${prefix} Cropped Photos Count`),
      totalPhotos
    )
  })

  // Preserve legacy `null` for empty photo count (set after the percentages
  // above have used it as a numeric denominator).
  if (merged['Photo Count'] === 0) {
    ratios['Photo Count'] = null
  }

  return ratios
}

// Keys not claimed by any declared strategy or derived above are passed through
// only when safe (all partials agree / only one has them), and collected so new
// fields don't silently become group analytics, e.g. identical
// "Cart Items - Product IDs" passes through while a brand-new field is warned.
// Declared keys come straight from the consts so a new strategy bucket needs no
// second registration here.
function collectUncategorizedProps(
  trackingDatas: OrderTrackingData[],
  merged: OrderTrackingData
): StrategyResult & { fallbackKeys: string[] } {
  const declaredKeys = Object.values(MERGE_TRACKING_DATA_STRATEGIES).flat()
  const handled = new Set([...declaredKeys, ...Object.keys(merged)])

  const uncategorized = new Set<string>()
  trackingDatas.forEach((d) => {
    Object.keys(d).forEach((k) => {
      if (!handled.has(k)) uncategorized.add(k)
    })
  })

  const values: OrderTrackingData = {}
  const conflicts: Conflict[] = []
  const fallbackKeys: string[] = []
  uncategorized.forEach((key) => {
    const resolved = resolveAgreedValue(trackingDatas, key)
    if (!resolved) return
    fallbackKeys.push(key)
    if ('conflict' in resolved) conflicts.push(resolved.conflict)
    else values[key] = resolved.value
  })

  return { values, conflicts, fallbackKeys }
}

// Combines the Order Completed tracking data built for each sub-order.
// 1. Known fields use explicit rules, e.g. sum revenue/counts, union arrays, keep shared values.
// 2. Dynamic AI-template revenue fields are summed by name, e.g. "AI Templates Portrait USD Revenue".
// 3. Group IDs come from the group response, e.g. Order Group UID and Order UIDs.
// 4. Percentages are recalculated from the full cart, e.g. photos in both books and tiles.
// 5. Unknown fields are kept only when safe, e.g. identical "Cart Items - Product IDs"; warn so stable fields get classified.
export function combineSubOrderTrackingDataForGroup(
  trackingDatas: OrderTrackingData[],
  orderGroup: OrderGroup,
  orders: InnerOrder[]
): OrderTrackingData {
  if (trackingDatas.length === 0) {
    return {}
  }

  const declaredProps = applyDeclaredStrategies(trackingDatas)
  const baseProps: OrderTrackingData = {
    ...declaredProps.values,
    ...mergeDynamicAiTemplateRevenue(trackingDatas),
    ...buildGroupIdentifiers(orderGroup, orders),
  }
  const propsWithRatios: OrderTrackingData = {
    ...baseProps,
    ...computeDerivedRatios(baseProps),
  }
  const fallbackProps = collectUncategorizedProps(
    trackingDatas,
    propsWithRatios
  )

  const conflicts = [...declaredProps.conflicts, ...fallbackProps.conflicts]
  if (fallbackProps.fallbackKeys.length > 0 || conflicts.length > 0) {
    logger.warning(
      '[cart-analytics] combineSubOrderTrackingDataForGroup: uncategorized keys used fallback — add an explicit strategy (sum/union/first/sameValue/anyTrue/firstPresent/derived).',
      { fallbackKeys: fallbackProps.fallbackKeys, conflicts }
    )
  }

  return { ...propsWithRatios, ...fallbackProps.values }
}
